export const CHUNK_RECOVERY_STORAGE_KEY = "hrp_chunk_recovery_attempted";
export const CHUNK_RECOVERY_RETRY_WINDOW_MS = 2 * 60 * 1000;
export const CHUNK_RECOVERY_RELOAD_DELAY_MS = 2_000;

const vitePreloadErrorPayloads = new WeakSet();

const STALE_CHUNK_MESSAGE_REGEX = /Unexpected token '<'/i;
const STATIC_CHUNK_PATH_REGEX = /\/static\/js\/.+\.chunk\.js(?:$|\?)/i;
// Matches both Vite 4 ("Loading CSS chunk X failed") and
// Vite 5 ("Unable to preload CSS for /assets/X") error formats.
const CSS_CHUNK_LOAD_ERROR_REGEX = /Loading CSS chunk .+ failed|Unable to preload CSS for .+/i;
// Matches Vite/ES module import errors that occur when stale cached HTML is
// served in place of a missing JS chunk after a new deploy. The thrown error
// wording differs by browser AND by how the import was triggered (preload
// vs. dynamic import()), so we match all known variants:
//   - "'text/html' is not a valid JavaScript MIME type." (older Chrome / Firefox preload)
//   - "Importing a module script failed." (Safari)
//   - "Failed to fetch dynamically imported module" (current Chrome import())
//   - "error loading dynamically imported module"   (current Firefox import())
//   - "Expected a JavaScript-or-Wasm module script" (Chrome strict-MIME diagnostic)
const VITE_MODULE_IMPORT_ERROR_REGEX =
    /is not a valid JavaScript MIME type|Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Expected a JavaScript(?:-or-Wasm)? module script/i;
// Matches React.lazy initializer failures that occur when a dynamic import()
// resolves to undefined/null instead of a valid ES module. This happens when a
// stale cached build tries to load a chunk that was replaced during deployment
// and the server returns a non-module response. React's lazy() initializer
// internally uses ._result to cache the resolved module; when that module is
// undefined, the attempt to access .default throws. Error message wording varies
// by browser engine:
//   Chrome V8: "Cannot read properties of undefined (reading 'default')"
//   Safari JSC: "undefined is not an object (evaluating 'e._result.default')"
//   Firefox SM: "e._result is undefined" (_result is React lazy's internal slot)
const LAZY_DEFAULT_EXPORT_ERROR_REGEX =
    /Cannot read properties of (undefined|null) \(reading 'default'\)|(undefined|null) is not an object \(evaluating .+\.default['"]?\)|_result is undefined/i;

// Matches TypeError from destructuring a dynamic import() result that resolved
// to undefined/null instead of a valid ES module namespace.  Stale cached pages
// can receive an error page (text/html) for a replaced chunk URL; some browsers
// resolve the module promise with an empty/undefined namespace rather than
// rejecting it.  Destructuring then throws:
//   "Cannot destructure property 'onCLS' of 'undefined' as it is undefined."
// The named property varies (e.g. 'onCLS', 'default', etc.), so we match the
// general pattern.
const DESTRUCTURING_UNDEFINED_ERROR_REGEX =
    /Cannot destructure property .+ of '?(undefined|null)'? as it is/i;

/**
 * Preserve the identity of payloads emitted by Vite's preload helper. Safari
 * reports an otherwise-ambiguous `TypeError: cancelled` when those fetches are
 * interrupted, so the message alone is not safe evidence of a stale chunk.
 */
export const markVitePreloadError = (payload) => {
    if ((typeof payload === "object" && payload !== null) || typeof payload === "function") {
        vitePreloadErrorPayloads.add(payload);
    }

    return payload;
};

export const isVitePreloadCancellation = (error) => (
    vitePreloadErrorPayloads.has(error) &&
    Object.prototype.toString.call(error) === "[object Error]" &&
    error.name === "TypeError" &&
    error.message === "cancelled"
);

const getScriptSourceFromErrorEvent = (event) => {
    if (!event) return "";
    return String(
        event.filename ||
        event.target?.src ||
        event.error?.fileName ||
        ""
    );
};

export const isStaleChunkSyntaxError = (event) => {
    if (!event) return false;

    const message = String(event.message || event.error?.message || "");
    const source = getScriptSourceFromErrorEvent(event);

    return (
        STALE_CHUNK_MESSAGE_REGEX.test(message) &&
        STATIC_CHUNK_PATH_REGEX.test(source)
    );
};

export const isCSSChunkLoadError = (event) => {
    if (!event) return false;

    const message = String(
        event.message || event.error?.message || event.reason?.message || ""
    );
    return CSS_CHUNK_LOAD_ERROR_REGEX.test(message);
};

export const isStaleChunkSyntaxErrorEvent = (event) => {
    const errorValue = String(event?.exception?.values?.[0]?.value || "");
    if (!STALE_CHUNK_MESSAGE_REGEX.test(errorValue)) {
        return false;
    }

    const frames = event?.exception?.values?.[0]?.stacktrace?.frames || [];
    return frames.some((frame) =>
        STATIC_CHUNK_PATH_REGEX.test(
            String(frame?.abs_path || frame?.absPath || frame?.filename || "")
        )
    );
};

export const isCSSChunkLoadErrorEvent = (event) => {
    const errorValue = String(event?.exception?.values?.[0]?.value || "");
    return CSS_CHUNK_LOAD_ERROR_REGEX.test(errorValue);
};

export const isViteModuleImportError = (event) => {
    if (!event) return false;

    const message = String(
        event.message || event.error?.message || event.reason?.message || ""
    );
    return VITE_MODULE_IMPORT_ERROR_REGEX.test(message);
};

export const isViteModuleImportErrorEvent = (event) => {
    const errorValue = String(event?.exception?.values?.[0]?.value || "");
    return VITE_MODULE_IMPORT_ERROR_REGEX.test(errorValue);
};

export const isLazyDefaultExportError = (event) => {
    if (!event) return false;

    const message = String(
        event.message || event.error?.message || event.reason?.message || ""
    );
    return LAZY_DEFAULT_EXPORT_ERROR_REGEX.test(message);
};

export const isLazyDefaultExportErrorEvent = (event) => {
    const errorValue = String(event?.exception?.values?.[0]?.value || "");
    return LAZY_DEFAULT_EXPORT_ERROR_REGEX.test(errorValue);
};

export const isDestructuringUndefinedError = (event) => {
    if (!event) return false;

    const message = String(
        event.message || event.error?.message || event.reason?.message || ""
    );
    return DESTRUCTURING_UNDEFINED_ERROR_REGEX.test(message);
};

export const isDestructuringUndefinedErrorEvent = (event) => {
    const errorValue = String(event?.exception?.values?.[0]?.value || "");
    return DESTRUCTURING_UNDEFINED_ERROR_REGEX.test(errorValue);
};

// Matches TypeErrors caused by Array.prototype.at() not being available in older
// browsers (Safari < 15.4, Chrome < 92).  The web-vitals v5 library uses .at(-1)
// inside its CLS and INP classes; the property name varies between minified builds
// (e.g. "this.o.at" or "this.u.at"), so we match the tail pattern only.
const WEB_VITALS_AT_ERROR_REGEX = /\.at is not a function/i;
// Matches the web-vitals chunk filename emitted by Vite (e.g. web-vitals-DTfazvTe.js)
const WEB_VITALS_CHUNK_FILENAME_REGEX = /web-vitals/i;

/**
 * Returns true when a Sentry event is a TypeError thrown inside the web-vitals
 * library because Array.prototype.at() is not available on the user's browser.
 *
 * The check is intentionally strict — ALL stack frames must originate from the
 * web-vitals chunk — so that legitimate app TypeErrors whose message happens to
 * contain ".at is not a function" are never silently dropped.
 */
export const isWebVitalsInternalErrorEvent = (event) => {
    if (!event) return false;

    const errorType = String(event?.exception?.values?.[0]?.type || "");
    const errorValue = String(event?.exception?.values?.[0]?.value || "");

    // Must be a TypeError (or untyped event) with an ".at is not a function" message
    if (errorType && errorType !== "TypeError") return false;
    if (!WEB_VITALS_AT_ERROR_REGEX.test(errorValue)) return false;

    // Guard: require at least one frame so we don't filter errors with no trace data
    const frames = event?.exception?.values?.[0]?.stacktrace?.frames || [];
    if (frames.length === 0) return false;

    // All frames must come from the web-vitals chunk
    return frames.every((frame) =>
        WEB_VITALS_CHUNK_FILENAME_REGEX.test(
            String(frame?.abs_path || frame?.absPath || frame?.filename || "")
        )
    );
};

export const getChunkRecoveryAttemptedAt = (storage) => {
    const value = storage.getItem(CHUNK_RECOVERY_STORAGE_KEY);
    if (!value) return null;

    const attemptedAt = Number(value);
    if (!Number.isFinite(attemptedAt) || attemptedAt <= 0) {
        return null;
    }

    return attemptedAt;
};

export const hasRecentChunkRecoveryAttempt = (storage, now = Date.now(), retryWindowMs = CHUNK_RECOVERY_RETRY_WINDOW_MS) => {
    const attemptedAt = getChunkRecoveryAttemptedAt(storage);
    if (!attemptedAt) return false;

    return now - attemptedAt < retryWindowMs;
};

export const attemptChunkRecoveryReload = (event, options = {}) => {
    const storage = options.storage ?? window.sessionStorage;
    const location = options.location ?? window.location;
    const now = options.now ?? Date.now();
    const retryWindowMs = options.retryWindowMs ?? CHUNK_RECOVERY_RETRY_WINDOW_MS;
    const reloadDelayMs = options.reloadDelayMs ?? 0;
    const setTimeoutFn = options.setTimeout ?? (
        typeof window !== "undefined" && typeof window.setTimeout === "function"
            ? window.setTimeout.bind(window)
            : undefined
    );

    if (
        !isStaleChunkSyntaxError(event) &&
        !isCSSChunkLoadError(event) &&
        !isViteModuleImportError(event) &&
        !isLazyDefaultExportError(event) &&
        !isDestructuringUndefinedError(event) &&
        !isVitePreloadCancellation(event?.error ?? event?.reason ?? event)
    ) {
        return false;
    }

    try {
        if (hasRecentChunkRecoveryAttempt(storage, now, retryWindowMs)) {
            return false;
        }
        storage.setItem(CHUNK_RECOVERY_STORAGE_KEY, String(now));
    } catch (_error) {
        // If sessionStorage is unavailable, still attempt one best-effort reload.
    }

    if (typeof location?.reload === "function") {
        if (reloadDelayMs > 0 && typeof setTimeoutFn === "function") {
            setTimeoutFn(() => location.reload(), reloadDelayMs);
            return true;
        }

        location.reload();
        return true;
    }

    return false;
};
